import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";

import { TriggerButton, TriggerButtonGroup } from "widgets";

import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import { useCountInvalidCells } from "../phenotype/EditingGrid.js";
import SampleEditingGrid, { validateNgsDataFiles } from "./SampleEditingGrid.js";
import { type IConfiguredPlaybook } from "@/app-utils/playbook.ts";
import {
  createHiddenAttributesWarning,
  fieldIsConfiguredInPlaybook,
  isAttributePartOfEditingGrid,
  mandatoryFieldHasNoDefault,
} from "@/app-utils/sample.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import { type Attribute, type SampleType } from "@/graphql.ts";
import { type IEditable } from "@/redux/modules/data-editing.ts";
import { getOrderedListOfTrackerAndWorkflowAttributes } from "@/redux/modules/playbook-execution.ts";
import {
  type IAttributesTemplatesLookup,
  type IDefaultValuesLookup,
  loadSampleInitialBatchData,
  setSamplesCountForRegistration,
  uniquenessValidations,
} from "@/redux/modules/sample-editing.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { KEYCODE, PLAYBOOK_TYPE } from "@/settings/settings.ts";
import { FormNote } from "@/ui-modules/core/form/layout/FormNote.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";

type ICombinedRegistrationNgsNewSampleGridScreenOwnProps = IPlaybookWizardStepProvidedProps;

/**
 * HOT-based editing grid for sample data
 *
 */
const MAX_NUMBER_ROWS = 1000;

const CombinedRegistrationNgsNewSampleGridScreenImpl: FC<ICombinedRegistrationNgsNewSampleGridScreenOwnProps> = (
  props: ICombinedRegistrationNgsNewSampleGridScreenOwnProps & IPlaybookWizardStepInjectedProps,
) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [validating, setValidating] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  const allAttributes = useSelector<IAppState, Attribute[]>((state) => state.sampleEditingState.sampleAttributes);
  const editingGrid = useSelector<IAppState, IEditable[]>((state) => state.sampleEditingState.editingGrid);
  const defaultValuesLookup = useSelector<IAppState, IDefaultValuesLookup>(
    (state) => state.sampleEditingState.defaultValuesLookup,
  );
  const templatesLookup = useSelector<IAppState, IAttributesTemplatesLookup>(
    (state) =>
      state.sampleEditingState.sampleType &&
      state.sampleEditingState.templatesLookup[state.sampleEditingState.sampleType.id],
  );
  const selectedSampleType = useSelector<IAppState, SampleType>((state) => state.sampleEditingState.sampleType);
  const playbook = useSelector<IAppState, IConfiguredPlaybook>((state) => state.playbookExecution.playbook);

  const proceedWithNextStep = () => {
    // additional validation about the files that is applicable only during a NGS Analysis playbook execution
    if (playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      const ngsDataFilesValidationError = validateNgsDataFiles(playbook, allAttributes, editingGrid);
      if (ngsDataFilesValidationError) {
        setErrorMessage(ngsDataFilesValidationError);
        return;
      }
    }

    setErrorMessage(null);
    setValidating(true);
    return dispatch(uniquenessValidations())
      .then(() => setValidating(false))
      .then(props.proceedWithNextStep)
      .catch((error: Error) => {
        setErrorMessage(error.message);
        setValidating(false);
      });
  };

  useEffect(() => {
    dispatch(loadSampleInitialBatchData()); // really happens only if there is no row yet
  }, []);

  const handleSampleNumberChange = useCallback((e) => {
    if (e.charCode !== KEYCODE.RETURN) {
      return;
    }
    // Make sure that the sample table has at least one row
    // Also limit the grid to 1000 samples
    const receivedNumber: number = e.target.value;
    const newSampleNumber: number =
      receivedNumber && receivedNumber > 0 ? (receivedNumber > MAX_NUMBER_ROWS ? MAX_NUMBER_ROWS : receivedNumber) : 1;
    dispatch(setSamplesCountForRegistration(newSampleNumber));
  }, []);

  const incrementSampleNumber = () => {
    if (editingGrid.length < MAX_NUMBER_ROWS) {
      dispatch(setSamplesCountForRegistration(editingGrid.length + 1));
    }
  };

  const decrementSampleNumber = () => {
    if (editingGrid.length > 1) {
      dispatch(setSamplesCountForRegistration(editingGrid.length - 1));
    }
  };

  const orderedListOfAttributes: Attribute[] = useMemo(() => {
    // the selection  does not contain all the required data (e.g. stored only the identifiers or so) therefore we need to flesh out this selection with the available data
    return getOrderedListOfTrackerAndWorkflowAttributes(playbook.config.sampleInput, allAttributes);
  }, [allAttributes]);

  const misConfiguredAttributes: Attribute[] = useMemo(() => {
    return allAttributes.filter(
      (attribute: Attribute) =>
        !attribute.isWorkflowParam && // misconfiguration applies for now only to tracker attributes
        !attribute.automatic &&
        !fieldIsConfiguredInPlaybook(attribute, playbook.config.sampleInput) &&
        mandatoryFieldHasNoDefault(attribute, defaultValuesLookup),
    );
  }, [orderedListOfAttributes, defaultValuesLookup]);

  const editingAttributes: Attribute[] = useMemo(() => {
    return orderedListOfAttributes
      .concat(misConfiguredAttributes)
      .filter((a) => isAttributePartOfEditingGrid(a, templatesLookup));
  }, [orderedListOfAttributes, misConfiguredAttributes, templatesLookup]);

  const invalidCellsCount: number = useCountInvalidCells(editingGrid);
  const multiValueWarning: string = createHiddenAttributesWarning(allAttributes);

  return (
    <PlaybookWizardStep
      {...props}
      wizardFooterData={{
        error:
          invalidCellsCount > 0
            ? `${invalidCellsCount} ${pluralizeWord("cell", invalidCellsCount)} ${pluralizeWord(
                "is",
                invalidCellsCount,
              )} invalid.`
            : errorMessage,
        nextButtonProps: {
          label: "Next",
          isLoading: validating,
          onPress: proceedWithNextStep,
          isDisabled: invalidCellsCount > 0,
        },
      }}
    >
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", rowGap: 15 }}>
        <div style={{ flex: "0 1 25px", display: "inline-flex", alignItems: "center" }}>
          Enter&nbsp;
          <TriggerButtonGroup>
            <TriggerButton
              icon={"remove"}
              label={""}
              aria-label={"Decrease"}
              onPress={decrementSampleNumber}
              data-action="minus"
            />
            <NumericFormat
              title="Number of Samples"
              value={editingGrid.length}
              onBlur={handleSampleNumberChange}
              onKeyPress={handleSampleNumberChange}
              displayType={"input"}
              allowNegative={false}
              decimalScale={0}
              className={"form-control"}
              key="numberOfRows"
              style={{
                display: "inline",
                textAlign: "center",
                width: 50,
                padding: "3px 5px",
                height: 26,
                minHeight: 26,
              }}
            />
            <TriggerButton
              icon={"add"}
              label={""}
              aria-label={"Increase"}
              onPress={incrementSampleNumber}
              data-action="plus"
            />
          </TriggerButtonGroup>
          &nbsp;samples of type&nbsp;<b>{selectedSampleType?.name}</b>
        </div>

        {multiValueWarning && <FormWarning>{multiValueWarning}</FormWarning>}

        <SampleEditingGrid attributes={editingAttributes} templatesLookup={templatesLookup} />

        {misConfiguredAttributes.length > 0 && (
          <FormNote>
            Some of the mandatory fields in this form are shown because of missing default configuration in{" "}
            <strong>{selectedSampleType.name}</strong>. Please contact your administrator.
          </FormNote>
        )}
      </div>
    </PlaybookWizardStep>
  );
};

export default CombinedRegistrationNgsNewSampleGridScreenImpl;
